import React from 'react';
import { createPortal } from 'react-dom';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { translate } from '../../../shared/translations.js';
import { IButtonProps } from '../Button/models/index.js';
import ClickLayer from '../ClickLayer/ClickLayer.js';
import Translate from '../Translate/Translate.js';

import { IModalConfiguration, IModalProps, IModalState } from './models/index.js';
import ModalAction from './styledComponents/ModalAction.js';
import ModalActionClose from './styledComponents/ModalActionClose.js';
import ModalActions from './styledComponents/ModalActions.js';
import ModalBody from './styledComponents/ModalBody.js';
import ModalCloseButton from './styledComponents/ModalCloseButton.js';
import ModalCloseIcon from './styledComponents/ModalCloseIcon.js';
import ModalFooter from './styledComponents/ModalFooter.js';
import ModalHead from './styledComponents/ModalHead.js';
import ModalOpenButton from './styledComponents/ModalOpenButton.js';
import ModalOpenIcon from './styledComponents/ModalOpenIcon.js';
import ModalTitle from './styledComponents/ModalTitle.js';
import ModalTitleBar from './styledComponents/ModalTitleBar.js';
import ModalWrapper from './styledComponents/ModalWrapper.js';
import { MODAL_CATEGORY_KEY, MODAL_COMPONENT_KEY } from './Modal.meta.js';
import { ModalHelper } from './ModalHelper.js';

export class Modal extends ComponentBase<IModalProps, IModalState, IModalConfiguration> {
	categoryKey = MODAL_CATEGORY_KEY;
	componentKey = MODAL_COMPONENT_KEY;

	showModal = false;

	modalNode: HTMLElement | null | undefined;

	get isOpen() {
		const { isOpen } = this.props;
		if (isOpen !== undefined) {
			return isOpen;
		}
		return this.showModal;
	}

	titleId: string;
	// Used to provide an accessible description for the modal when available.
	headerId: string;
	lastFocus: Element | undefined | null;

	constructor(props: IModalProps) {
		super(props);

		this.titleId = `modal-${props.modalId}-title-${common.commonUtils.generateGuid()}`;
		this.headerId = `modal-${props.modalId}-header-${common.commonUtils.generateGuid()}`;
		this.modalNode = document.getElementById(props.modalId || '');
		if (props.isOpen !== undefined) {
			this.handleIsOpenInitialization(props.isOpen);
		}
		ModalHelper.init();

		makeObservable(this, {
			showModal: observable,
			modalNode: observable.ref,
			isOpen: computed,
			titleBar: computed,
			titleBarCloseButton: computed,
			headerContent: computed,
			bodyContent: computed,
			footerContent: computed,
			actions: computed,
			openButton: computed,
			handleClose: action,
			handleOpen: action,
		});
	}

	componentDidUpdate(prevProps: IModalProps) {
		if (this.props.isOpen !== prevProps.isOpen) {
			this.handleIsOpenInitialization(this.props.isOpen);
		}
	}

	handleIsOpenInitialization(isOpen = false) {
		isOpen ? this.handleOpen() : this.handleClose();
	}

	/**
	 * Modal Title which is referenced by screen readers when the modal is opened.
	 * Additionally the close button is display here
	 *
	 * @readonly
	 */
	get titleBar() {
		const { title } = this.props;
		return (
			<ModalTitleBar>
				<ModalTitle id={this.titleId}>
					<Translate>{title}</Translate>
				</ModalTitle>
				{this.titleBarCloseButton}
			</ModalTitleBar>
		);
	}

	get titleBarCloseButton() {
		const { title, closeButtonProps } = this.props;
		const buttonProps = this.getModalButtonProps(closeButtonProps, {
			iconProps: {
				asset: 'base.modal.close',
			},
			iconPosition: 'right',
			disabled: false,
		});
		if (buttonProps.disabled) {
			return null;
		}
		const CloseIcon = <ModalCloseIcon {...buttonProps.iconProps} aria-hidden={true} />;
		return (
			<ModalCloseButton
				onClick={() => this.handleClose()}
				aria-label={translate(buttonProps.text || `Close ${title} dialog`)}
			>
				{buttonProps.iconPosition === 'left' && CloseIcon}
				{buttonProps.text && <Translate>{buttonProps.text}</Translate>}
				{buttonProps.iconPosition === 'right' && CloseIcon}
			</ModalCloseButton>
		);
	}

	get headerContent() {
		const { headerContent } = this.props;
		return headerContent && <ModalHead id={this.headerId}>{headerContent}</ModalHead>;
	}

	get bodyContent() {
		const { children, bodyContent } = this.props;
		return <ModalBody>{bodyContent || children}</ModalBody>;
	}

	get footerContent() {
		const { footerContent } = this.props;
		return footerContent && <ModalFooter>{footerContent}</ModalFooter>;
	}

	/**
	 * Typical modal action buttons that appear below the footer content
	 *
	 * @readonly
	 */
	get actions() {
		const { actions } = this.props;
		if (actions && actions.length > 0) {
			return (
				<ModalActions>
					{actions.map((modalAction, modalActionIndex) => {
						const ModalActionButton = modalAction.type === 'custom' ? ModalAction : ModalActionClose;
						return (
							<ModalActionButton
								key={`action-${modalActionIndex}-${modalAction.id}`}
								onClick={() => {
									switch (modalAction.type) {
										case 'close':
											this.handleClose();
											break;
										case 'closeAndCustom':
											this.handleClose();
											break;
										default:
											if (modalAction.onAction) {
												modalAction.onAction(modalAction.id);
											}
											break;
									}
								}}
							>
								<Translate>{modalAction.label}</Translate>
							</ModalActionButton>
						);
					})}
				</ModalActions>
			);
		}
		return null;
	}

	/**
	 * The button responsible for opening the modal
	 *
	 * @readonly
	 */
	get openButton() {
		const { title, openButtonProps } = this.props;
		const buttonProps = this.getModalButtonProps(openButtonProps, {
			text: `Open ${title} dialog`,
			iconProps: {
				asset: !this.isOpen ? 'base.modal.openIcon' : 'base.modal.openedIcon',
			},
			iconPosition: 'right',
			disabled: false,
		});
		if (buttonProps.disabled) {
			return null;
		}
		const ExpandIcon = <ModalOpenIcon {...buttonProps.iconProps} aria-hidden={'true'} />;
		return (
			<ModalOpenButton onClick={() => this.handleOpen()}>
				{buttonProps.iconPosition === 'left' && ExpandIcon}
				<Translate>{buttonProps.text}</Translate>
				{buttonProps.iconPosition === 'right' && ExpandIcon}
			</ModalOpenButton>
		);
	}

	/**
	 * Used by the static ModalHelper class
	 *
	 * @readonly
	 */

	handleClose = () => {
		const { onClose } = this.props;
		this.showModal = false;
		ModalHelper.removeActiveModal(this);
		if (onClose) {
			onClose();
		}
	};
	handleOpen = () => {
		this.showModal = true;
		ModalHelper.addActiveModal(this);
	};

	getModalButtonProps(props: IButtonProps | undefined, defaultProps: IButtonProps): IButtonProps {
		if (props) {
			return { ...defaultProps, ...props };
		}
		return defaultProps;
	}

	renderModal() {
		const { className } = this.props;
		return (
			<>
				<ModalWrapper
					className={className}
					role="dialog"
					aria-modal="true"
					aria-labelledby={this.titleId}
					aria-describedby={this.headerContent && this.headerId}
				>
					{this.titleBar}
					{this.headerContent}
					{this.bodyContent}
					{this.footerContent}
					{this.actions}
				</ModalWrapper>
				<ClickLayer onClick={() => this.handleClose()} />
			</>
		);
	}

	renderModalPortal() {
		return createPortal(this.renderModal(), this.modalNode as HTMLElement);
	}

	render() {
		const modal = this.modalNode ? this.renderModalPortal() : this.renderModal();
		return (
			<>
				{this.isOpen && modal}
				{this.openButton}
			</>
		);
	}
}

export default InjectedStyledComponent(observer(Modal), MODAL_CATEGORY_KEY, MODAL_COMPONENT_KEY);
